import { useCallback, useEffect, useRef, useState } from "react";
import { useTaskSettings } from "./use-task-settings";

export const useAssignments = (type, assignmentParams) => {
    const { preserveDefaultFor } = useTaskSettings();
    const initialAssignmentParams = useRef(assignmentParams);
    const [assignmentOptions, setAssignmentOptions] = useState(assignmentParams ? assignmentParams.availableAssignments : []);
    const [userAssignmentOptions, setUserAssignmentOptions] = useState(assignmentParams ? assignmentParams.users : []);

    const populateUsers = useCallback((teamAssignments, assignmentGroupKey) => {
        const reduceUniqueUsers = (teams) => {
            return teams
                .reduce((userAssignmentGroups, { users }) => {
                    return userAssignmentGroups.concat(users);
                }, [])
                .map(({ userId, fullName, email }) => {
                    return {
                        text: fullName,
                        value: userId,
                        email: email,
                    };
                })
                .filter((uniqueUser, index, allUsers) => {
                    return index === allUsers.findIndex(({ value }) => value === uniqueUser.value);
                })
                .sort((a, b) => (a.text > b.text ? 1 : -1));
        };
        const uniqueUsers = reduceUniqueUsers(teamAssignments);

        if (!assignmentGroupKey) {
            return uniqueUsers;
        }

        const group = teamAssignments.filter((assignmentGroup) => assignmentGroup.assignmentGroupKey === assignmentGroupKey);

        return reduceUniqueUsers(group);
    }, []);

    useEffect(() => {
        if (assignmentParams) {
            initialAssignmentParams.current = assignmentParams;
            setAssignmentOptions(assignmentParams.availableAssignments);
            setUserAssignmentOptions(assignmentParams.users);
        }
    }, [assignmentParams]);

    const populateUserAssignments = useCallback((selectedUserId, teams) => {
        const assignments = initialAssignmentParams.current.availableUsersAssignments.filter(({ users }) => {
            return users.some(({ userId }) => (selectedUserId ? userId.toString() === selectedUserId : !!selectedUserId));
        });
        const narrowedOptions = teams.filter(({ value }) => assignments.some(({ assignmentGroupKey }) => assignmentGroupKey === value));

        const buildUserAssignments = () => {
            return narrowedOptions.length === 0 ? initialAssignmentParams.current.availableAssignments : narrowedOptions;
        };

        return buildUserAssignments().sort((a, b) => (a.text > b.text ? 1 : -1));
    }, []);

    const updateTeamAssignments = useCallback(
        (assignmentGroupKey, skipPreserveDefault) => {
            if (!assignmentGroupKey && !skipPreserveDefault) {
                preserveDefaultFor(type);
            }

            const groupKey = assignmentGroupKey === "null" ? undefined : assignmentGroupKey;
            const newOptions = populateUsers(initialAssignmentParams.current.availableUsersAssignments, groupKey);
            setUserAssignmentOptions(newOptions);
        },
        [populateUsers, preserveDefaultFor, type]
    );

    const updateUserAssignments = useCallback(
        (selectedUserId, skipPreserveDefault) => {
            if (!selectedUserId && !skipPreserveDefault) {
                preserveDefaultFor(type);
            }

            const newOptions = populateUserAssignments(selectedUserId, assignmentOptions);
            setAssignmentOptions(newOptions);
        },
        [type, preserveDefaultFor, assignmentOptions, populateUserAssignments]
    );

    return {
        initialAssignmentParams: initialAssignmentParams.current,
        assignmentOptions,
        userAssignmentOptions,
        updateUserAssignments,
        updateTeamAssignments,
        populateUserAssignments,
        populateUsers,
    };
};
